import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useUserRole, type AppRole } from "@/hooks/useUserRole";
import { useAuth } from "@/hooks/useAuth";
import {
  LayoutDashboard,
  Users,
  ArrowLeftRight,
  ShieldCheck,
  Gift,
  Banknote,
  AlertTriangle,
  Settings as SettingsIcon,
  Store,
  ScrollText,
  LogOut,
  ArrowLeft,
  BarChart3,
  IdCard,
  Gavel,
  Briefcase,
  Bluetooth,
  Megaphone,
  Mail,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { SystemStatusBanner } from "@/components/SystemStatusBanner";

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  allowedRoles: AppRole[];
}

const ANY_ADMIN: AppRole[] = ["admin", "super_admin", "finance_admin", "support_admin", "merchant_admin", "moderator"];
const FINANCE: AppRole[] = ["admin", "super_admin", "finance_admin"];
const SUPPORT: AppRole[] = ["admin", "super_admin", "support_admin"];
const SUPER: AppRole[] = ["admin", "super_admin"];

const NAV: NavItem[] = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, allowedRoles: ANY_ADMIN },
  { to: "/admin/users", label: "Users", icon: Users, allowedRoles: [...SUPPORT, "moderator"] },
  { to: "/admin/transactions", label: "Transactions", icon: ArrowLeftRight, allowedRoles: FINANCE },
  { to: "/admin/referrals", label: "Referrals", icon: ShieldCheck, allowedRoles: [...SUPPORT, "moderator"] },
  { to: "/admin/kyc", label: "KYC", icon: IdCard, allowedRoles: FINANCE },
  { to: "/admin/rewards", label: "Rewards", icon: Gift, allowedRoles: FINANCE },
  { to: "/admin/withdrawals", label: "Withdrawals", icon: Banknote, allowedRoles: FINANCE },
  { to: "/admin/fraud", label: "Fraud", icon: AlertTriangle, allowedRoles: SUPPORT },
  { to: "/admin/disputes", label: "Disputes", icon: Gavel, allowedRoles: SUPPORT },
  { to: "/admin/merchants", label: "Merchants", icon: Store, allowedRoles: [...SUPER, "merchant_admin"] },
  { to: "/admin/reports", label: "Reports", icon: BarChart3, allowedRoles: FINANCE },
  { to: "/admin/hustle-hub", label: "Hustle Hub", icon: Briefcase, allowedRoles: [...SUPPORT, ...FINANCE] },
  { to: "/admin/bluetooth", label: "Bluetooth", icon: Bluetooth, allowedRoles: [...SUPPORT, ...FINANCE] },
  { to: "/admin/ads", label: "Ads", icon: Megaphone, allowedRoles: [...SUPER, "merchant_admin"] },
  { to: "/admin/waitlist", label: "Waitlist", icon: Mail, allowedRoles: SUPER },
  { to: "/admin/audit", label: "Audit log", icon: ScrollText, allowedRoles: SUPER },
  { to: "/admin/system", label: "System", icon: SettingsIcon, allowedRoles: SUPER },
];

export const AdminLayout = () => {
  const { roles, hasAny } = useUserRole();
  const { signOut } = useAuth();
  const navigate = useNavigate();

  const visible = NAV.filter((n) => hasAny(n.allowedRoles));

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <SystemStatusBanner />
      <div className="flex flex-1 min-h-0">
        {/* Sidebar */}
        <aside className="hidden md:flex w-60 shrink-0 flex-col border-r border-border bg-card">
          <div className="px-5 py-4 border-b border-border">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Cash Isle</div>
            <div className="text-base font-semibold">Admin Console</div>
          </div>
          <nav className="flex-1 p-3 space-y-1 overflow-auto">
            {visible.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/admin"}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
                    isActive
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  )
                }
              >
                <item.icon className="h-4 w-4" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>
          <div className="p-3 border-t border-border space-y-2">
            <div className="px-3 text-xs text-muted-foreground">
              Roles: {roles.length ? roles.join(", ") : "—"}
            </div>
            <Button variant="outline" size="sm" className="w-full" onClick={() => navigate("/wallet")}>
              <ArrowLeft className="h-4 w-4" /> Back to app
            </Button>
            <Button variant="ghost" size="sm" className="w-full" onClick={signOut}>
              <LogOut className="h-4 w-4" /> Sign out
            </Button>
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 min-w-0 flex flex-col">
          <header className="md:hidden flex items-center justify-between px-4 py-3 border-b border-border bg-card">
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Cash Isle</div>
              <div className="text-sm font-semibold">Admin</div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate("/wallet")}>
              <ArrowLeft className="h-4 w-4" /> App
            </Button>
          </header>

          <div className="md:hidden overflow-x-auto border-b border-border bg-card">
            <div className="flex gap-1 px-2 py-2 min-w-max">
              {visible.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/admin"}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-2 px-3 py-1.5 rounded-md text-xs whitespace-nowrap",
                      isActive
                        ? "bg-primary/10 text-primary font-medium"
                        : "text-muted-foreground hover:bg-muted",
                    )
                  }
                >
                  <item.icon className="h-3.5 w-3.5" />
                  {item.label}
                </NavLink>
              ))}
            </div>
          </div>

          <div className="flex-1 overflow-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};
